import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

import { INameId } from '@modeso/types__aumico-lib-core-be';
import { SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { ISuperTableAddFreeColumnDialogResult } from '../../interfaces/super-table-add-free-column-dialog-result.interface';
import { SuperTableColumnWidth } from '@modeso/aumico-lib-notes-fe/enums/SuperTableColumnWidth.ebum';

@UntilDestroy()
@Component({
    selector: 'super-table-add-free-column-form',
    templateUrl: './super-table-add-free-column-form.dialog.html',
    styleUrls: ['./super-table-add-free-column-form.dialog.scss'],
})
export class SuperTableAddFreeColumnComponent implements OnInit {
    public form: UntypedFormGroup | undefined;

    @Input() public columnData: ISuperTableAddFreeColumnDialogResult | undefined;

    dataTypeSelections: INameId[] = [];
    columnWidthSelections: INameId[] = [];
    belongingYearSelections: INameId[] = [];

    @Output() public formValueChanged = new EventEmitter<ISuperTableAddFreeColumnDialogResult>();
    @Output() public formStatusChange = new EventEmitter<boolean>();


    showInfoDialog: boolean = false;
    
    constructor(private localizedTexts: I18NextService) {
        this.prepareDataTypeSelections();
        this.prepareBelongingYearSelections();
        this.prepareColumnWidthSelections();
    }

    ngOnInit(): void {
        this.initializeForm();
    }

    private initializeForm(): void {
        const selectedObjectValidator: ValidatorFn = (control: AbstractControl) => {
            const v = control.value;
            if (v == null) return { required: true };
            // Valid only if it's an object with an id (i.e., picked from dropdown)
            return typeof v === 'object' && v?.id != null ? null : { invalidOption: true };
        };

        if (this.columnData != null) {
            this.form = new UntypedFormGroup({
                title: new UntypedFormControl(this.columnData.title),
                dataType: new UntypedFormControl(
                    this.dataTypeSelections.find((selection) => selection.id === this.columnData.dataType),
                    [Validators.required, selectedObjectValidator],
                ),
                belongingYear: new UntypedFormControl(
                    this.belongingYearSelections.find((selection) => selection.id === this.columnData.belongingYear),
                    [Validators.required, selectedObjectValidator],
                ),
                columnWidth: new UntypedFormControl(
                    this.columnWidthSelections.find((selection) => selection.id === this.columnData.columnWidth),
                    [Validators.required, selectedObjectValidator],
                ),
            });
        } else {
            this.form = new UntypedFormGroup({
                title: new UntypedFormControl(undefined),
                dataType: new UntypedFormControl(undefined, [Validators.required, selectedObjectValidator]),
                belongingYear: new UntypedFormControl(undefined, [Validators.required, selectedObjectValidator]),
                columnWidth: new UntypedFormControl(undefined, [Validators.required, selectedObjectValidator]),
            });
        }

        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            const result: ISuperTableAddFreeColumnDialogResult = {
                title: value.title,
                dataType: value?.dataType?.id,
                belongingYear: value?.belongingYear?.id,
                columnWidth: value?.columnWidth?.id,
            };
            this.formValueChanged.emit(result);
        });

        // Watch dataType changes to auto-fill columnWidth
        this.form.get('dataType')?.valueChanges.pipe(untilDestroyed(this)).subscribe((dataType) => {
            const columnWidthControl = this.form?.get('columnWidth');
            if (dataType && dataType.id && !columnWidthControl?.value) {
                // Auto-fill based on data type
                if (dataType.id === SuperTableColumnType.NUMBER) {
                    // Auto-fill to STANDARD for numbers
                    const standardOption = this.columnWidthSelections.find(option => option.id === SuperTableColumnWidth.STANDARD);
                    columnWidthControl?.setValue(standardOption);
                } else if (dataType.id === SuperTableColumnType.TEXT) {
                    // Auto-fill to DYNAMIC for text
                    const dynamicOption = this.columnWidthSelections.find(option => option.id === SuperTableColumnWidth.DYNAMIC);
                    columnWidthControl?.setValue(dynamicOption);
                }
            }
        });

        this.form.statusChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (value === 'VALID') {
                this.formStatusChange.emit(true);
            } else {
                this.formStatusChange.emit(false);
            }
        });
    }

    private prepareDataTypeSelections(): void {
        this.dataTypeSelections.push(
            {
                id: SuperTableColumnType.TEXT,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_text') as string,
            },
            {
                id: SuperTableColumnType.NUMBER,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_type_number') as string,
            },
        );
    }

    private prepareBelongingYearSelections(): void {
        this.belongingYearSelections.push(
            {
                id: SuperTableColumnBelongingYear.CURRENT_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_current') as string,
            },
            {
                id: SuperTableColumnBelongingYear.PREVIOUS_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_previous') as string,
            },
            {
                id: SuperTableColumnBelongingYear.NO_YEAR,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_belonging_year_none') as string,
            },
        );
    }
    private prepareColumnWidthSelections(): void {
        this.columnWidthSelections.push(
            {
                id: SuperTableColumnWidth.DYNAMIC,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_dynamic') as string,
            },
            {
                id: SuperTableColumnWidth.SMALL,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_small') as string,
            },
            {
                id: SuperTableColumnWidth.STANDARD,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_standard') as string,
            },
            {
                id: SuperTableColumnWidth.LARGE,
                name: this.localizedTexts.t('aumico_notes_super_table_options_col_width_large') as string,
            },
        );
    }
}
